import React, { useState } from "react";
import classNames from "classnames";

import {
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Input,
  Badge,
} from "reactstrap";
import InputMask from "react-input-mask";
import { toastr } from "react-redux-toastr";
import Select from "react-select";

import { INPUTS } from "./approverInputOptions";
import { warn } from "../../../utils/logger";
import { humanizeGroup } from "../../../utils/normalize";

const PhoneInput = ({ options, newApprover, onChange }) => (
  <InputMask
    mask="+1 (999) 999-9999"
    maskChar={null}
    value={newApprover[options.param] || ""}
    onChange={({ target }) => onChange(target)}
  >
    {(inputProps) => (
      <Input bsSize="lg" name={options.param} placeholder={options.placeholder} {...inputProps} />
    )}
  </InputMask>
);

const ApproverModal = ({ isOpen, users, thisUser, selected, toggleModal, applyChanges }) => {
  const [newApprover, setNewApprover] = useState({
    userId_bint: null,
    firstname_vch: "",
    lastname_vch: "",
    email_vch: "",
    sms_number_vch: "",
    phone_number_vch: "",
    ...selected,
  });

  // If not an internal user, filter out internal users as selectable
  if (thisUser.group_id_bint < 5 || ["user", "admin", "super"].includes(thisUser.email_vch)) {
    warn("NOT an internal user - hiding internal users from selection");
    users = users.filter((user) => user.group_id_bint < 5);
  }

  const closeBtn = (
    <button className="close" onClick={toggleModal}>
      &times;
    </button>
  );

  function selectUser(user) {
    if (user) {
      setNewApprover({
        ...newApprover,
        userId_bint: user.user_id_bint,
        firstname_vch: user.firstname_vch,
        lastname_vch: user.lastname_vch,
        email_vch: user.email_vch,
      });
    } else {
      setNewApprover({
        userId_bint: null,
        firstname_vch: "",
        lastname_vch: "",
        email_vch: "",
        sms_number_vch: "",
        phone_number_vch: "",
      });
    }
  }

  function updateApprover({ name, value }) {
    if (/name/.test(name)) {
      value = value.replace(/[^a-z-'.\s]/i, "");
    }
    if (/number/.test(name)) {
      value = value.replace(/[^\d]/g, "");
    }

    setNewApprover({
      ...newApprover,
      [name]: value,
    });
  }

  function validateChanges(e) {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!newApprover.firstname_vch) {
      return toastr.error("Oops", "Please enter a first name");
    }
    if (!newApprover.lastname_vch) {
      return toastr.error("Oops", "Please enter a last name");
    }
    if (!newApprover.email_vch && !newApprover.sms_number_vch && !newApprover.phone_number_vch) {
      return toastr.error("Oops", "Please enter at least one contact method: Email, SMS, Phone");
    }

    applyChanges(newApprover).then((success) => {
      if (success) {
        toggleModal(false);
      }
    });
  }

  return (
    <Modal isOpen={isOpen} toggle={toggleModal}>
      <ModalHeader toggle={toggleModal} close={closeBtn}>
        {newApprover.approverId_bint ? "Edit" : "Create"} Approver
      </ModalHeader>

      <ModalBody>
        <Row className="mb-2">
          <Col>
            <Select
              isSearchable
              isClearable
              className="react-select-container"
              classNamePrefix="react-select"
              options={users.filter((user) => user.group_id_bint > 1)}
              value={newApprover.firstname_vch ? newApprover : null}
              placeholder="Select a user to make an approver"
              onChange={selectUser}
              formatOptionLabel={(user, { context }) => {
                return context === "value" ? (
                  <span className="w-100">{`${user.firstname_vch} ${user.lastname_vch}`}</span>
                ) : (
                  <span className="w-100">
                    {`${user.firstname_vch} ${user.lastname_vch}`}
                    <Badge className="float-right">{humanizeGroup(user.group_id_bint)}</Badge>
                  </span>
                );
              }}
              getOptionLabel={(user) => user.user_id_bint}
              getOptionValue={(user) => user.user_id_bint}
              filterOption={(option, searchText) =>
                option.data.user_id_bint === searchText ||
                option.data.firstname_vch.toLowerCase().includes(searchText.toLowerCase()) ||
                option.data.lastname_vch.toLowerCase().includes(searchText.toLowerCase())
              }
            />
            {!newApprover.firstname_vch && (
              <small className="text-danger">Please select a user</small>
            )}
          </Col>
        </Row>

        <hr />

        <Row>
          {INPUTS.map((input) => (
            <Col key={input.param} className={classNames("mb-4", input.classNames)}>
              <InputGroup>
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>
                    {input.label}
                    {input.isRequired && <span className="text-danger">*</span>}
                  </InputGroupText>
                </InputGroupAddon>
                {input.type === "phone" ? (
                  <PhoneInput options={input} newApprover={newApprover} onChange={updateApprover} />
                ) : (
                  <Input
                    bsSize="lg"
                    type={input.type || "text"}
                    name={input.param}
                    value={newApprover[input.param] || ""}
                    onChange={({ target }) => updateApprover(target)}
                    placeholder={input.placeholder}
                    required={input.isRequired}
                    disabled={input.disabled}
                  />
                )}
              </InputGroup>
            </Col>
          ))}
        </Row>

        <Row>
          <Col className="text-right text-muted">
            <small>Note: The name fields will be populated for you when a user is selected</small>
          </Col>
        </Row>
      </ModalBody>

      <ModalFooter className="d-flex justify-content-between">
        <Button color="secondary" onClick={toggleModal}>
          Cancel
        </Button>
        <Button color="primary" onClick={validateChanges}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ApproverModal;
